import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Card,
  Col,
  DatePicker,
  Empty,
  PageHeader,
  Row,
  Spin,
  Tag,
  Tooltip,
  message,
  Table,
} from "antd";
import { DownloadOutlined, FilePdfOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import "moment/locale/es";

// pdfmake
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

import ButtonItem from "../../../Components/ButtonItem";
import SelectEmpleadosItem from "../../../Components/SelectEmpleadosItem";
import SelectDepartamentosItem from "../../../Components/SelectDepartamentosItem";
import {
  construirHojaTabla,
  descargarLibro,
  limpiarNombreArchivo,
  texto,
  centro,
  fecha,
  numero,
} from "../../../Components/ExcelTablaReporte";

pdfMake.vfs = pdfFonts.pdfMake.vfs;
moment.locale("es");

const { RangePicker } = DatePicker;

class PrimeroUltimo extends Component {
  state = {
    departamentoId: null,
    departamentoNombre: "",
    empleadoId: null,
    empleadoNombre: "",
    rango: [moment().startOf("month"), moment().endOf("month")],

    loading: false,
    rows: [], // [{ nombre, departamento, fecha, diaSemana, primeraChecada, ultimaChecada, tiempoTotal }]
  };

  // =========================
  // Helpers
  // =========================
  validarRangoMax120 = (rangoMoment) => {
    if (!rangoMoment || !rangoMoment[0] || !rangoMoment[1]) return true;
    const dias = rangoMoment[1].startOf("day").diff(rangoMoment[0].startOf("day"), "days") + 1;
    return dias <= 120;
  };

  disabledDate = (current) => {
    const { rango } = this.state;
    const start = rango?.[0];
    const end = rango?.[1];

    if (start && !end) {
      const min = moment(start).subtract(119, "days").startOf("day");
      const max = moment(start).add(119, "days").endOf("day");
      return current && (current.isBefore(min) || current.isAfter(max));
    }

    if (!start && end) {
      const min = moment(end).subtract(119, "days").startOf("day");
      const max = moment(end).add(119, "days").endOf("day");
      return current && (current.isBefore(min) || current.isAfter(max));
    }

    return false;
  };

  fmtFecha = (x) => {
    if (!x) return "—";
    return moment(x).isValid() ? moment(x).format("YYYY-MM-DD") : (x || "—");
  };

  fmtDiaSemana = (row) => {
    // Si backend ya lo manda, úsalo; si no, calcúlalo.
    if (row?.diaSemana) return row.diaSemana;
    const f = row?.fecha;
    if (!f) return "—";
    return moment(f).isValid() ? moment(f).format("dddd") : "—";
  };

  fmtHora = (x) => {
    // backend puede mandar "08:55" o Date; intentamos normalizar
    if (!x) return "—";
    if (typeof x === "string") return x;
    if (moment(x).isValid()) return moment(x).format("HH:mm");
    return "—";
  };

  fmtTiempoTotal = (x) => {
    // tiempo total en horas decimal (ej: 9.5)
    if (x === null || x === undefined || x === "") return "—";
    const n = Number(x);
    if (Number.isNaN(n)) return String(x);
    return n.toFixed(1);
  };

  // =========================
  // API
  // =========================
  consultar = async () => {
    const { empresaId, headersToken } = this.props;
    const { rango, departamentoId, empleadoId } = this.state;

    if (!rango?.[0] || !rango?.[1]) {
      message.warning("Selecciona un rango de fechas.");
      return;
    }

    if (!this.validarRangoMax120(rango)) {
      message.warning("El rango no puede ser mayor a 120 días.");
      return;
    }

    this.setState({ loading: true });

    try {
      // Backend esperado:
      // POST reporteAsistencia/primeroUltimo
      // body: { empresaId, departamentoId?, empleadoId?, fechaInicio, fechaFin }
      // resp: { success: 2, data: [...] }
      const res = await axios.post(
        "reportesAsistencia/primeroUltimo",
        {
          empresaId,
          departamentoId: departamentoId || null,
          empleadoId: empleadoId || null,
          fechaInicio: rango[0].format("YYYY-MM-DD"),
          fechaFin: rango[1].format("YYYY-MM-DD"),
        },
        { headers: headersToken }
      );

      if (res.data?.success !== 2) {
        this.setState({ rows: [] });
        return;
      }

      const data = Array.isArray(res.data?.data) ? res.data.data : [];

      // Normalizamos llaves por si el backend manda distinto
      const rows = data.map((r) => ({
        nombre: r.nombre || r.empleadoNombre || r.empleado || "—",
        departamento: r.departamento || r.departamentoNombre || "—",
        fecha: r.fecha || r.dia || r.fechaTexto || null,
        diaSemana: r.diaSemana || r.diaSemanaTexto || null,
        primeraChecada: r.primeraChecada || r.entrada || r.primera || null,
        ultimaChecada: r.ultimaChecada || r.salida || r.ultima || null,
        tiempoTotal: r.tiempoTotal ?? r.horas ?? r.totalHoras ?? null,
      }));

      this.setState({ rows });
    } catch (e) {
      this.setState({ rows: [] });
      message.error("No se pudo cargar el reporte.");
    } finally {
      this.setState({ loading: false });
    }
  };

  // =========================
  // Events
  // =========================
  onChangeDepartamento = (value, option) => {
    const departamentoNombre =
      option?.label || option?.children || option?.nombre || option?.title || "";

    // Al cambiar depto, limpio empleado para evitar incoherencias
    this.setState(
      {
        departamentoId: value || null,
        departamentoNombre: departamentoNombre || "",
        empleadoId: null,
        empleadoNombre: "",
      },
      () => this.consultar()
    );
  };

  onChangeEmpleado = (value, option) => {
    const empleadoNombre =
      option?.label || option?.children || option?.nombre || option?.title || "";
    this.setState({ empleadoId: value || null, empleadoNombre: empleadoNombre || "" }, () =>
      this.consultar()
    );
  };

  onChangeRango = (value) => {
    if (!value || !value[0] || !value[1]) {
      this.setState({ rango: value }, () => this.consultar());
      return;
    }
    if (!this.validarRangoMax120(value)) {
      message.warning("El rango no puede ser mayor a 120 días.");
      return;
    }
    this.setState({ rango: value }, () => this.consultar());
  };

  // =========================
  // EXPORT: Excel
  // =========================
  exportExcel = () => {
    const { rows, rango, departamentoNombre, empleadoNombre } = this.state;

    if (!rows?.length) {
      message.warning("No hay datos para exportar.");
      return;
    }

    const columnas = [
      { titulo: "Nombre", ancho: 30 },
      { titulo: "Departamento", ancho: 20 },
      { titulo: "Fecha", ancho: 12 },
      { titulo: "Día de Semana", ancho: 14 },
      { titulo: "Primera Checada", ancho: 15 },
      { titulo: "Última Checada", ancho: 15 },
      { titulo: "Tiempo Total", ancho: 12 },
    ];

    const filas = rows.map((r) => [
      texto(r.nombre),
      texto(r.departamento),
      r.fecha ? fecha(this.fmtFecha(r.fecha)) : centro("—"),
      texto(this.fmtDiaSemana(r)),
      centro(this.fmtHora(r.primeraChecada)),
      centro(this.fmtHora(r.ultimaChecada)),
      numero(this.fmtTiempoTotal(r.tiempoTotal)),
    ]);

    const descripcionFiltros = [
      `Departamento: ${departamentoNombre || "Todos"}`,
      `Empleado: ${empleadoNombre || "Todos"}`,
      `Del ${rango?.[0]?.format("DD/MM/YYYY")} al ${rango?.[1]?.format("DD/MM/YYYY")}`,
    ].join(" · ");

    descargarLibro(
      [
        {
          nombre: "Primera y Última Checada",
          hoja: construirHojaTabla({ columnas, filas, totales: [] }, {
            titulo: "Primera y Última Checada",
            empresaNombre: this.props.empresa,
            descripcionFiltros,
          }),
        },
      ],
      limpiarNombreArchivo(`primero_ultimo_${rango[0].format("YYYYMMDD")}_${rango[1].format("YYYYMMDD")}`)
    );
  };

  // =========================
  // EXPORT: PDF (pdfMake)
  // =========================
  exportPDF = () => {
    const { rows, rango, departamentoNombre, empleadoNombre } = this.state;

    if (!rows?.length) {
      message.warning("No hay datos para exportar.");
      return;
    }

    const body = [
      [
        { text: "Nombre", bold: true },
        { text: "Departamento", bold: true },
        { text: "Fecha", bold: true },
        { text: "Día", bold: true },
        { text: "Primera", bold: true },
        { text: "Última", bold: true },
        { text: "Total", bold: true },
      ],
    ];

    rows.forEach((r) => {
      body.push([
        r.nombre || "—",
        r.departamento || "—",
        this.fmtFecha(r.fecha),
        this.fmtDiaSemana(r),
        this.fmtHora(r.primeraChecada),
        this.fmtHora(r.ultimaChecada),
        this.fmtTiempoTotal(r.tiempoTotal),
      ]);
    });

    const docDefinition = {
      pageSize: "A4",
      pageOrientation: "landscape",
      pageMargins: [25, 45, 25, 35],
      content: [
        { text: "Reporte: Primera y Última Checada", style: "title" },
        {
          columns: [
            {
              width: "*",
              text: [
                { text: "Departamento: ", bold: true },
                departamentoNombre || "Todos",
                "\n",
                { text: "Empleado: ", bold: true },
                empleadoNombre || "Todos",
              ],
            },
            {
              width: "auto",
              text: [
                { text: "Rango: ", bold: true },
                `${rango?.[0]?.format("DD/MM/YYYY")} - ${rango?.[1]?.format("DD/MM/YYYY")}`,
              ],
            },
          ],
          margin: [0, 6, 0, 10],
        },
        {
          table: {
            headerRows: 1,
            widths: [110, 90, 70, 80, 60, 60, 50],
            body,
          },
          layout: "lightHorizontalLines",
          fontSize: 8.5,
        },
        {
          text: `Generado: ${moment().format("DD/MM/YYYY HH:mm")}`,
          style: "footer",
          margin: [0, 10, 0, 0],
        },
      ],
      styles: {
        title: { fontSize: 14, bold: true },
        footer: { fontSize: 9, color: "#666666" },
      },
      defaultStyle: { fontSize: 10 },
    };

    pdfMake
      .createPdf(docDefinition)
      .download(`primero_ultimo_${rango[0].format("YYYYMMDD")}_${rango[1].format("YYYYMMDD")}.pdf`);
  };

  // =========================
  // UI
  // =========================
  getColumns = () => [
    {
      title: "Nombre",
      dataIndex: "nombre",
      key: "nombre",
      width: 180,
      ellipsis: true,
      sorter: (a, b) => (a.nombre || "").localeCompare(b.nombre || ""),
    },
    {
      title: "Departamento",
      dataIndex: "departamento",
      key: "departamento",
      width: 140,
      ellipsis: true,
      sorter: (a, b) => (a.departamento || "").localeCompare(b.departamento || ""),
    },
    {
      title: "Fecha",
      dataIndex: "fecha",
      key: "fecha",
      width: 110,
      render: (v) => this.fmtFecha(v),
      sorter: (a, b) => {
        const da = moment(a.fecha).isValid() ? moment(a.fecha).valueOf() : 0;
        const db = moment(b.fecha).isValid() ? moment(b.fecha).valueOf() : 0;
        return da - db;
      },
      defaultSortOrder: "ascend",
    },
    {
      title: "Día de Semana",
      key: "diaSemana",
      width: 140,
      render: (_, r) => this.fmtDiaSemana(r),
      sorter: (a, b) => (this.fmtDiaSemana(a) || "").localeCompare(this.fmtDiaSemana(b) || ""),
    },
    {
      title: "Primera Checada",
      dataIndex: "primeraChecada",
      key: "primeraChecada",
      width: 130,
      render: (v) => this.fmtHora(v),
      sorter: (a, b) => (this.fmtHora(a.primeraChecada) || "").localeCompare(this.fmtHora(b.primeraChecada) || ""),
    },
    {
      title: "Última Checada",
      dataIndex: "ultimaChecada",
      key: "ultimaChecada",
      width: 130,
      render: (v) => this.fmtHora(v),
      sorter: (a, b) => (this.fmtHora(a.ultimaChecada) || "").localeCompare(this.fmtHora(b.ultimaChecada) || ""),
    },
    {
      title: "Tiempo Total",
      dataIndex: "tiempoTotal",
      key: "tiempoTotal",
      width: 110,
      align: "right",
      render: (v) => this.fmtTiempoTotal(v),
      sorter: (a, b) => (Number(a.tiempoTotal) || 0) - (Number(b.tiempoTotal) || 0),
    },
  ];

  renderTabla() {
    const { rows, loading } = this.state;

    if (!rows?.length) {
      return (
        <div style={{ padding: 20 }}>
          <Empty description="Selecciona filtros y consulta para ver resultados" />
        </div>
      );
    }

    return (
      <Table
        size="small"
        rowKey={(r, idx) => `${r.nombre}-${r.departamento}-${this.fmtFecha(r.fecha)}-${idx}`}
        columns={this.getColumns()}
        dataSource={rows}
        loading={loading}
        pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: [20, 50, 100, 200] }}
        scroll={{ x: 980, y: "calc(100vh - 330px)" }}
      />
    );
  }

  render() {
    const { loading, rango, departamentoId, empleadoId, rows } = this.state;

    return (
      <>
        <PageHeader
          title="Primera y Última Checada"
          style={{
            border: "1px solid rgb(235, 237, 240)",
            backgroundColor: "white",
            marginBottom: 10,
          }}
          extra={
            <>
              <Tooltip title="Consultar">
                <ButtonItem icon={<ReloadOutlined />} onClick={this.consultar} loading={loading}>
                  Consultar
                </ButtonItem>
              </Tooltip>

              <Tooltip title="Exportar a Excel">
                <ButtonItem
                  type="default"
                  icon={<DownloadOutlined />}
                  onClick={this.exportExcel}
                  disabled={!rows?.length}
                >
                  Excel
                </ButtonItem>
              </Tooltip>

              <Tooltip title="Exportar a PDF">
                <ButtonItem
                  type="default"
                  icon={<FilePdfOutlined />}
                  onClick={this.exportPDF}
                  disabled={!rows?.length}
                >
                  PDF
                </ButtonItem>
              </Tooltip>
            </>
          }
        />

        <Row gutter={10}>
          {/* Panel filtros */}
          <Col xs={24} md={8}>
            <Card
              title="Filtros"
              size="small"
              style={{
                minHeight: "80vh",
                borderRadius: 6,
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Departamento</div>

                <SelectDepartamentosItem
                  empresaId={this.props.empresaId}
                  headersToken={this.props.headersToken}
                  sinBorde
                  dropdownMatchSelectWidth={600}
                  value={departamentoId}
                  onChange={this.onChangeDepartamento}
                  allowClear
                  width="100%"
                  placeholder="Departamentos"
                />

                <div style={{ fontSize: 12, color: "#999", marginTop: 6 }}>
                  Tip: si eliges departamento, puedes dejar empleado en “Todos”.
                </div>
              </div>

              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Empleado</div>

                <SelectEmpleadosItem
                  empresaId={this.props.empresaId}
                  headersToken={this.props.headersToken}
                  sinBorde
                  dropdownMatchSelectWidth={600}
                  value={empleadoId}
                  onChange={this.onChangeEmpleado}
                  allowClear
                  width="100%"
                  placeholder="Empleados"
                  // Si tu SelectEmpleadosItem soporta filtrar por depto:
                  // departamentoId={departamentoId}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>
                  Rango de fechas (máx. 120 días)
                </div>

                <RangePicker
                  value={rango}
                  onChange={this.onChangeRango}
                  style={{ width: "100%" }}
                  format="DD/MM/YYYY"
                  disabledDate={this.disabledDate}
                  allowClear={false}
                />

                <div style={{ fontSize: 12, color: "#999", marginTop: 8 }}>
                  Tip: cambia filtros y se recarga automáticamente (o usa “Consultar”).
                </div>
              </div>
            </Card>
          </Col>

          {/* Tabla */}
          <Col xs={24} md={16}>
            <Card
              title="Resultados"
              size="small"
              extra={loading ? <Tag color="processing">Cargando...</Tag> : <Tag>Registros: {rows?.length || 0}</Tag>}
              bodyStyle={{ padding: 0 }}
              style={{
                minHeight: "80vh",
                borderRadius: 6,
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <Spin spinning={loading}>{this.renderTabla()}</Spin>
            </Card>
          </Col>
        </Row>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  empresa: state.empresa.nombre,
});

export default connect(mapStateToProps)(PrimeroUltimo);